import React, { useEffect, useState, type FC, type ReactElement } from 'react'
import { Dismiss16Regular, SubtractCircle16Regular } from '@fluentui/react-icons'
import { Popover, Flex, Button } from 'antd'

import { type IRelationshipRequest } from '../../../constants/types/generatedTypesAliases'
import { type IUserPermission } from '../../../constants/types/common'
import { showNotification } from '../../../constants/helpers/common'
import { useDeleteRelationshipsMutation } from '../../../state/apis/generated/relationshipsApi'

let removePermissionsPopoverId = 0
let activeRemovePermissionsPopoverId: number | null = null
const removePermissionsPopoverListeners = new Set<(activeId: number | null) => void>()

const notifyRemovePermissionsPopoverListeners = () => {
  removePermissionsPopoverListeners.forEach(listener => listener(activeRemovePermissionsPopoverId))
}

const setActiveRemovePermissionsPopoverId = (popoverId: number | null) => {
  activeRemovePermissionsPopoverId = popoverId
  notifyRemovePermissionsPopoverListeners()
}

interface IRemovePermissionsPopover {
  selectedRelationships: IUserPermission[]
  children?: ReactElement
  selectedUserId: string
  removePermissionsCallback?: () => void
}

export const RemovePermissionsPopover: FC<IRemovePermissionsPopover> = ({
  selectedRelationships,
  selectedUserId,
  children,
  removePermissionsCallback
}) => {
  const [removePermissions] = useDeleteRelationshipsMutation()
  const [removePermissionsVisible, setRemovePermissionsVisible] = useState<boolean>(false)
  const [popoverId] = useState<number>(() => ++removePermissionsPopoverId)

  useEffect(() => {
    const currentPopoverId = popoverId

    const handleActivePopoverChange = (activeId: number | null) => {
      setRemovePermissionsVisible(activeId === currentPopoverId)
    }

    removePermissionsPopoverListeners.add(handleActivePopoverChange)
    handleActivePopoverChange(activeRemovePermissionsPopoverId)

    return () => {
      removePermissionsPopoverListeners.delete(handleActivePopoverChange)

      if (activeRemovePermissionsPopoverId === currentPopoverId) {
        setActiveRemovePermissionsPopoverId(null)
      }
    }
  }, [popoverId])

  const handleOpenChange = (open: boolean) => {
    /* istanbul ignore else */
    if (open) {
      setActiveRemovePermissionsPopoverId(popoverId)
      return
    }

    /* istanbul ignore next */
    if (activeRemovePermissionsPopoverId === popoverId) {
      setActiveRemovePermissionsPopoverId(null)
    }
  }

  const handleRemovePermissions = async () => {
    const relationships: IRelationshipRequest[] = selectedRelationships.map(r => ({
      ...r,
      resourceType: `${r.namespace}/${r.resourceType}`,
      userId: selectedUserId
    }))
    const result = await removePermissions({
      serviceAuthorisationDomainModelsSpiceDbRequestsSpiceDbWriteRelationshipsRequest: {
        relationships
      }
    })

    showNotification({ result, success: 'Permissions removed from the user' })
    setRemovePermissionsVisible(false)

    if (removePermissionsCallback) {
      removePermissionsCallback()
    }
  }

  return (
    <Popover
      trigger='click'
      open={removePermissionsVisible}
      onOpenChange={handleOpenChange}
      data-testid='remove-permissions-modal'
      classNames={{
        body: 'popover-body'
      }}
      title={(
        <Flex
          justify='space-between'
          align='center'>
          Remove Permissions
          <Button
            type='text'
            icon={<Dismiss16Regular />}
            className='btn-space'
            aria-label='Dismiss Button'
            onClick={() => setRemovePermissionsVisible(false)}
          />
        </Flex>
      )}
      content={(
        <div>
          <p>Are you sure you want to remove
            <br /> these permissions?
          </p>
          <Flex justify='flex-end'>
            <Button
              type='text'
              onClick={() => setRemovePermissionsVisible(false)}>
              Cancel
            </Button>
            <Button
              color='danger'
              variant='text'
              onClick={handleRemovePermissions}>
              Remove
            </Button>
          </Flex>
        </div>
      )}>
      {
        children
          ? children
          : (
              <Button
                color='danger'
                variant='text'
                className='btn-space'
                icon={<SubtractCircle16Regular />}>
                Remove Permissions
              </Button>
            )
      }
    </Popover>
  )
}
